import { useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  House,
  Package,
  MapPin,
  Gear,
  List,
  X,
  ArrowSquareOut,
  CurrencyRub,
  SignOut,
  User,
  Storefront,
  Headset,
  ShieldCheck,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const navItems = [
  { path: "/", label: "Дашборд", icon: House },
  { path: "/stocks", label: "Остатки", icon: Package },
  { path: "/clusters", label: "Кластеры", icon: MapPin },
  { path: "/products", label: "Товары", icon: Storefront },
  { path: "/finance", label: "Финансы", icon: CurrencyRub },
];

export const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const getPageTitle = () => {
    const item = navItems.find((nav) => nav.path === location.pathname);
    if (location.pathname === '/support') return "Поддержка";
    if (location.pathname === '/profile') return "Профиль";
    if (location.pathname === '/settings/ozon-api-guide') return "Подключение Ozon API";
    if (location.pathname.startsWith('/admin')) return "Админ-панель";
    return item?.label || "Ozon Stock Manager";
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="app-container">
      {/* Mobile menu overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`sidebar ${mobileMenuOpen ? "fixed inset-y-0 left-0 z-50" : "hidden md:flex"}`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#005BFF] flex items-center justify-center">
              <Package size={24} weight="bold" className="text-white" />
            </div>
            <div>
              <h1 className="font-chivo font-bold text-white text-lg tracking-tight">
                Tactic Seller
              </h1>
            </div>
          </div>
          {/* Mobile close button */}
          <button
            className="absolute top-4 right-4 p-2 text-zinc-400 hover:text-white md:hidden"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="close-mobile-menu"
          >
            <X size={24} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1" data-testid="navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `nav-item ${isActive ? "active" : ""}`
              }
              onClick={() => setMobileMenuOpen(false)}
              data-testid={`nav-${item.path === "/" ? "dashboard" : item.path.slice(1)}`}
            >
              <item.icon size={20} weight={location.pathname === item.path ? "fill" : "regular"} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User info & Footer */}
        <div className="p-4 border-t border-zinc-800 space-y-3">
          {/* User dropdown */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800 transition-colors text-left">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                    {user.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {user.name}
                    </p>
                    <p className="text-xs text-zinc-500 truncate">
                      {user.email}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Мой аккаунт</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Профиль</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Gear className="mr-2 h-4 w-4" />
                  <span>Настройки</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/support')}>
                  <Headset className="mr-2 h-4 w-4" />
                  <span>Поддержка</span>
                </DropdownMenuItem>
                {user?.is_admin && (
                  <DropdownMenuItem onClick={() => navigate('/admin')}>
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    <span>Админ</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <SignOut className="mr-2 h-4 w-4" />
                  <span>Выйти</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          <a
            href="https://seller.ozon.ru"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
            data-testid="ozon-seller-link"
          >
            <ArrowSquareOut size={16} />
            <span>Ozon Seller</span>
          </a>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        {/* Header */}
        <header className="app-header">
          <button
            className="mobile-menu-toggle md:hidden"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="open-mobile-menu"
          >
            <List size={24} />
          </button>
          <h2 className="font-chivo font-bold text-xl text-zinc-900 ml-4 md:ml-0">
            {getPageTitle()}
          </h2>
          
          {/* Desktop user menu */}
          <div className="ml-auto hidden md:flex items-center gap-4">
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                      {user.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <span className="text-sm">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="mr-2 h-4 w-4" />
                    <span>Профиль</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Gear className="mr-2 h-4 w-4" />
                    <span>Настройки</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/support')}>
                    <Headset className="mr-2 h-4 w-4" />
                    <span>Поддержка</span>
                  </DropdownMenuItem>
                  {user?.is_admin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      <ShieldCheck className="mr-2 h-4 w-4" />
                      <span>Админ</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <SignOut className="mr-2 h-4 w-4" />
                    <span>Выйти</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </header>

        {/* Page content */}
        <div className="content-area">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <Outlet />
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Layout;
